'use client';

import { TrackComponent } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import {
  ListingMore,
  useListingPaging,
} from '@eliadmin/core/src/components/nt/category/listing/listing-paging';
import { ListingSort } from '@eliadmin/core/src/components/nt/category/listing/listing-sort';
import { ProductTile } from '@eliadmin/core/src/components/nt/product/product-tile';
import { ProductListSchema } from '@eliadmin/core/src/components/seo/product-list-schema';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import type { CategoryTexts } from '@eliadmin/core/src/utils/nt/category';
import { stripTags } from '@eliadmin/core/src/utils/nt/text';
import { cn } from '@eliadmin/core/src/utils/style-utils';

import { SEARCH_GRID, TILE_SIZES } from './search-skeleton';
import type { useSearchProducts } from './use-search-products';

type SearchListingProps = {
  /** The typed query – the id of the tracked list. */
  search: string;
  /** Loaded result of `useSearchProducts` (a response exists). */
  result: ReturnType<typeof useSearchProducts>;
  texts: CategoryTexts;
  /** „Produkty (142)“ – the heading of the list for assistive technology. */
  heading: string;
};

/**
 * Listing of the search result – the toolbar, grid, „show more“ and pagination of the category
 * listing (Figma 121:1677) without the filters: „Zobrazujem 1–12 z 142 produktov“ and the sort menu,
 * the product tiles, „Zobraziť ďalších 12 produktov“ (appends the next page) and the pagination.
 * Sort and page live in the scoped category store (URL `order`, `orderBy`, `page`). Like the core
 * search page every shown page reports `view_item_list` of the list `search:<query>` and the
 * products are in the list schema.
 */
export function SearchListing({ search, result, texts, heading }: SearchListingProps) {
  const { query, pages, products, perPage, meta, isUpdating, loadMore } = result;
  const currency = useGlobalStore(state => state.generalLayout?.currency);
  const { top, showing, remaining, goToPage } = useListingPaging({
    meta,
    count: products.length,
    perPage,
    showingText: texts.showing,
  });

  return (
    <div ref={top} className="flex min-w-0 scroll-mt-6 flex-col gap-6">
      <h2 className="sr-only">{heading}</h2>
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <p aria-live="polite" className="nt-body-m min-h-[19px] text-[#363D36]">
          {showing}
        </p>
        <ListingSort recommended={stripTags(texts.recommended)} />
      </div>

      <ProductListSchema products={products} currency={currency} />
      {pages.map(page => (
        <TrackComponent
          key={`${search}:${page.meta.current_page}`}
          eventName="view_item_list"
          eventParams={{ item_list_id: `search:${search}`, item_list_name: 'search' }}
          items={page.data}
        />
      ))}
      <ul
        role="list"
        aria-busy={isUpdating}
        className={cn(
          SEARCH_GRID,
          'transition-opacity motion-reduce:transition-none',
          isUpdating && 'pointer-events-none opacity-50',
        )}
      >
        {products.map((product, index) => (
          <li key={`${product.id}-${index}`} className="flex">
            <ProductTile product={product} imageSizes={TILE_SIZES} />
          </li>
        ))}
      </ul>

      {meta && products.length ? (
        <ListingMore
          meta={meta}
          remaining={remaining}
          hasNextPage={query.hasNextPage}
          isFetchingNextPage={query.isFetchingNextPage}
          isUpdating={isUpdating}
          onMore={loadMore}
          onPage={goToPage}
          texts={texts}
        />
      ) : null}
    </div>
  );
}
